import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'

import { AvatarWithBadge } from '@/components/ui/avatar'
import { IconArrowLeft, IconHelp, IconInputSearch } from '@/components/ui/icons'
import { useAuth } from '@/hooks/useAuth'
import { useCanGoBack } from '@/hooks/useCanGoBack'
import { cn } from '@/lib/utils'

interface AppHeaderProps {
  title: string
  /** Opens the navigation drawer; only shown below 1200px. */
  onOpenMenu: () => void
  /** Opens the user panel (settings dialog) from the avatar. */
  onOpenUserPanel: () => void
  unreadCount?: number
  /** Row-2 left: the back control (Figma `action`, a 20px arrow-left). */
  onBack?: (() => void) | undefined
  /** Row-2 left: the search field. Omit to leave it out of the row. */
  searchPlaceholder?: string | undefined
  /**
   * Hands the search field to the Explore panel: the field stops accepting
   * input and opens the palette instead, the way the Figma frames show it
   * (2267:290683 — Explore over Home, Home's own field behind it).
   */
  onSearchClick?: (() => void) | undefined
  /** Row-2 right: page controls — filter/sort/view on Discover, share/more on a detail page. */
  actions?: ReactNode
  /** Row-1 right: shows the help (?) button next to the avatar when set. */
  onHelp?: (() => void) | undefined
  /** Accessible label of the help button. Defaults to `nav.help`. */
  helpLabel?: string | undefined
  /** Figma node this header instance implements. */
  nodeId?: string
  onSearchChange?: ((value: string) => void) | undefined
}

/**
 * Figma `button-md` / `button-lg` in the icon-only, pill shape.
 * 44px below 1200px, 36px from 1200px up.
 */
export function HeaderIconButton({
  children,
  label,
  onClick,
  className,
  ...rest
}: {
  children: ReactElement
  label: string
  onClick?: (() => void) | undefined
  className?: string
} & Record<`data-${string}`, string>): ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'bg-btn-secondary-base hub-stroke text-btn-icon flex shrink-0 cursor-pointer items-center justify-center',
        'hover:bg-btn-border rounded-full p-3 transition-colors lg:p-2',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}

function initialsOf(name: string, email: string): string {
  const source = name.trim() || email
  const parts = source.split(/[\s.@]+/).filter(Boolean)
  const letters = parts.slice(0, 2).map((part) => part.charAt(0))
  return letters.join('').toUpperCase() || '?'
}

/**
 * True once the page has been scrolled down from the top. Only the scroll
 * container that actually holds the header (or the window) counts — scrolling
 * inside a popup or any other inner list is ignored.
 */
function useIsScrolled(headerRef: RefObject<HTMLElement | null>): boolean {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = (event: Event): void => {
      const target = event.target

      if (target instanceof Element) {
        const header = headerRef.current
        if (!header || !target.contains(header)) return
        setScrolled(target.scrollTop > 0)
        return
      }

      setScrolled((window.scrollY || document.documentElement.scrollTop) > 0)
    }

    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [headerRef])

  return scrolled
}

/**
 * Application header — Figma `header` (3195:10337), the component every screen
 * shares. Row 1 is the title plus help/avatar; row 2 is a slot the page fills:
 * search on Home, back + search + filter/sort/view on Discover, back +
 * share/more on the news and event detail pages.
 *
 * The row-2 group only stacks below 900px when there is a search field to make
 * room for — the detail pages keep back and actions on one line at every width
 * (Figma 2452:101947).
 */
export function AppHeader({
  title,
  onOpenMenu,
  onOpenUserPanel,
  unreadCount = 0,
  onBack,
  searchPlaceholder,
  onSearchClick,
  actions,
  onHelp,
  helpLabel,
  nodeId = '2256:281970',
  onSearchChange,
}: AppHeaderProps): ReactElement {
  const { t } = useTranslation()
  const { user } = useAuth()
  const headerRef = useRef<HTMLElement>(null)
  const scrolled = useIsScrolled(headerRef)
  const canGoBack = useCanGoBack()

  const showBack = Boolean(onBack) && canGoBack
  const hasLeadingRow = showBack || Boolean(searchPlaceholder)
  const hasSecondRow = hasLeadingRow || Boolean(actions)

  return (
    <header
      ref={headerRef}
      data-node-id={nodeId}
      data-name="header"
      className={cn(
        'bg-header sticky top-0 z-30 flex w-full flex-col items-start gap-3 border-b px-4 pt-4 pb-2 transition-colors',
        scrolled ? 'border-[#d4d4d4] dark:border-[#333333]' : 'border-transparent',
      )}
    >
      <div className="flex w-full items-center justify-between lg:pl-1">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            aria-label={t('nav.openMenu')}
            onClick={onOpenMenu}
            className="bg-btn-secondary-base hub-stroke text-btn-icon flex shrink-0 items-center justify-center rounded-full p-3 transition-colors hover:brightness-95 lg:hidden"
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>

          <h1 className="hub-display-lg-highlight text-header-text truncate">{title}</h1>
        </div>

        <div data-name="action" className="flex shrink-0 items-center gap-2">
          {onHelp && (
            <HeaderIconButton label={helpLabel ?? t('nav.help')} onClick={onHelp}>
              <IconHelp className="size-5" aria-hidden="true" />
            </HeaderIconButton>
          )}

          <button
            type="button"
            onClick={onOpenUserPanel}
            aria-label={t('settings.title')}
            className="shrink-0 cursor-pointer rounded-full"
          >
            <AvatarWithBadge
              initials={initialsOf(user?.name ?? '', user?.email ?? '')}
              count={0}
              label={t('nav.notifications', { count: unreadCount })}
            />
          </button>
        </div>
      </div>

      {hasSecondRow && (
        <div
          data-name="action"
          className={cn(
            'flex w-full items-center gap-2',
            // With a search field the row splits in two below 900px, exactly as
            // the 400/600 Discover frames show. Without one it never splits.
            searchPlaceholder && 'flex-col md:flex-row',
            hasLeadingRow ? 'justify-between' : 'justify-end',
          )}
        >
          {hasLeadingRow && (
            <div
              data-name="action"
              className={cn('flex items-center gap-2', searchPlaceholder && 'w-full md:w-auto')}
            >
              {showBack && (
                <HeaderIconButton label={t('actions.back')} onClick={onBack}>
                  <IconArrowLeft className="size-5" aria-hidden="true" />
                </HeaderIconButton>
              )}

              {searchPlaceholder && (
                <label className="bg-input-surface hub-stroke-input flex w-full min-w-0 items-center gap-1 rounded-full py-3 pr-3 pl-2 md:w-65 lg:py-2">
                  <IconInputSearch className="text-input-icon size-5 shrink-0" aria-hidden="true" />
                  <input
                    type="search"
                    placeholder={searchPlaceholder}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    {...(onSearchClick
                      ? { readOnly: true, onFocus: onSearchClick, onClick: onSearchClick }
                      : {})}
                    className="hub-search-input hub-label-md text-widget-primary placeholder:text-input-text h-5 w-full min-w-0 bg-transparent outline-none"
                  />
                </label>
              )}
            </div>
          )}

          {actions}
        </div>
      )}
    </header>
  )
}
